import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  AssessmentDto,
  ConventionId,
  ConventionSupportedJwt,
  DeleteAssessmentRequestDto,
  LegacyAssessmentDto,
  SignAssessmentRequestDto,
} from "shared";
import type {
  PayloadActionWithFeedbackTopic,
  PayloadActionWithFeedbackTopicError,
} from "src/core-logic/domain/feedback/feedback.slice";

export interface AssessmentState {
  isLoading: boolean;
  currentAssessment: AssessmentDto | LegacyAssessmentDto | null;
}

const initialState: AssessmentState = {
  isLoading: false,
  currentAssessment: null,
};

export const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    creationRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        assessment: AssessmentDto;
        jwt: ConventionSupportedJwt;
      }>,
    ) => {
      state.isLoading = true;
    },
    creationSucceeded: (state, _action: PayloadActionWithFeedbackTopic) => {
      state.isLoading = false;
    },
    creationFailed: (state, _action: PayloadActionWithFeedbackTopicError) => {
      state.isLoading = false;
    },
    deleteAssessmentRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        params: DeleteAssessmentRequestDto;
        jwt: ConventionSupportedJwt;
      }>,
    ) => {
      state.isLoading = true;
    },
    deleteAssessmentSucceeded: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        conventionId: ConventionId;
        jwt: ConventionSupportedJwt;
      }>,
    ) => {
      state.isLoading = false;
    },
    deleteAssessmentFailed: (
      state,
      _action: PayloadActionWithFeedbackTopicError,
    ) => {
      state.isLoading = false;
    },
    getAssessmentRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        conventionId: ConventionId;
        jwt: string;
      }>,
    ) => {
      state.isLoading = true;
    },
    getAssessmentSucceeded: (
      state,
      action: PayloadAction<AssessmentDto | LegacyAssessmentDto>,
    ) => {
      state.isLoading = false;
      state.currentAssessment = action.payload;
    },
    noAssessmentForConventionFound: (state) => {
      state.isLoading = false;
    },
    getAssessmentFailed: (
      state,
      _action: PayloadActionWithFeedbackTopicError,
    ) => {
      state.isLoading = false;
    },
    signAssessmentRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        params: SignAssessmentRequestDto;
        jwt: ConventionSupportedJwt;
      }>,
    ) => {
      state.isLoading = true;
    },
    signAssessmentSucceeded: (
      state,
      _action: PayloadActionWithFeedbackTopic,
    ) => {
      state.isLoading = false;
    },
    signAssessmentFailed: (
      state,
      _action: PayloadActionWithFeedbackTopicError,
    ) => {
      state.isLoading = false;
    },
    clearFetchedAssessment: (state) => {
      state.currentAssessment = initialState.currentAssessment;
    },
  },
});
